// Waypoint Configuration - Reusable component for tour creation and editing
import React, { useState } from 'react';
import { Card, CardContent, Box, Typography, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import WaypointList from './WaypointList';
import WaypointSelectionDialog from './WaypointSelectionDialog';

interface WaypointListItem {
  id: string;
  name?: string;
  destination?: string;
  /** @deprecated use name */
  content_name?: string;
  /** @deprecated use destination */
  waypoint_name?: string;
  version: number;
  floor: number;
  is_halfway: boolean;
  is_return_point: boolean;
}

interface WaypointConfigurationProps {
  waypoints: WaypointListItem[];
  onWaypointsChange: (waypoints: WaypointListItem[]) => void;
  museumCode: string;
  museumName?: string;
}

const WaypointConfiguration: React.FC<WaypointConfigurationProps> = ({
  waypoints,
  onWaypointsChange,
  museumCode,
  museumName,
}) => {
  const [waypointDialogOpen, setWaypointDialogOpen] = useState(false);
  const [availableWaypoints, setAvailableWaypoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const handleAddWaypoint = async () => {
    try {
      setLoading(true);
      const { tourService } = await import('../../tour/services/tourService');
      const waypointsData = await tourService.getWaypointsByMuseum(museumCode);
      setAvailableWaypoints(waypointsData);
      setWaypointDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch waypoints:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWaypointConfirm = (selectedWaypoints: any[]) => {
    const newWaypoints = selectedWaypoints.map(selectedWaypoint => ({
      ...selectedWaypoint,
      id: `${selectedWaypoint.waypoint_name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }));
    onWaypointsChange([...waypoints, ...newWaypoints]);
  };

  const handleRemoveWaypoint = (index: number) => {
    const newWaypoints = waypoints.filter((_, i) => i !== index);
    onWaypointsChange(newWaypoints);
  };

  const handleReorderWaypoints = (newWaypoints: WaypointListItem[]) => {
    onWaypointsChange(newWaypoints);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <Card sx={{ flex: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Waypoint Map
            </Typography>
            {museumCode === 'CCM' || museumCode === 'ACM' ? (
              <iframe
                src={`${process.env.REACT_APP_RAAS_BASE_URL || 'https://nhb-raas-staging.senserbot.com'}/api-ws/routing/?hide=true&showNames=true&scale=90&museum=${museumCode}`}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '900px',
                  border: 'none',
                }}
                title="Waypoint Routing Map"
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '400px',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  border: '1px dashed rgba(0, 0, 0, 0.12)',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Map not available for {museumName || museumCode}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Waypoint Configuration</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddWaypoint}>
                Add Waypoint
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure the waypoints for this tour. You can drag to reorder them.
            </Typography>
            <WaypointList waypoints={waypoints} onReorder={handleReorderWaypoints} onRemove={handleRemoveWaypoint} />
          </CardContent>
        </Card>
      </Box>

      <WaypointSelectionDialog
        open={waypointDialogOpen}
        onClose={() => setWaypointDialogOpen(false)}
        onConfirm={handleWaypointConfirm}
        availableWaypoints={availableWaypoints}
        loading={loading}
      />
    </>
  );
};

export default WaypointConfiguration;
